// Gate + hand-off tool the tenant-profiling playbook calls with its
// canvas-side output. After sampling the tenant's data and characterizing
// what the org does with AI, the agent calls this once with a small set
// of tailored canvases — each a named bundle of EXISTING kernel commands
// with tenant-grounded titles, descriptions, and captions. The tool
// validates the set and live-runs the referenced kernels, but persists
// NOTHING: the accepted set lives in the scout chat's transcript, and the
// Tenant profile view offers each canvas as a click-to-add entry that
// materializes as a normal, editable, deletable user canvas
// (see TenantProfileView in ChatConsole). Auto-mounting a read-only set
// was rejected — deleting or re-tailoring left orphaned read-only rows.

import { Type } from "@earendil-works/pi-ai";
import { coerceJsonArg } from "./coerceJsonArg.js";
import { normalizeRows } from "./canvasLayout.js";
import { sanitizeSlug } from "./canvasTemplateStore.js";
import { CanvasRunScheduler } from "./canvasRunScheduler.js";
import {
    listKernelCatalog,
    resolveKernelByCommandAsync,
} from "./kernelStore.js";
import { runKernel } from "./kernelRunner.js";
import { KERNEL_RUN_CANCEL_REASON } from "./kernelRunOutcome.js";
import {
    RUN_SURFACES,
    asResolvedKernel,
    createUnknownRunContext,
} from "./kernelResolver.js";

// Every tailored slug carries this prefix so a profiling run can never
// mint a slug that collides with a shipped built-in canvas, and so the
// origin of the canvas stays legible in URLs and the admin page.
const SLUG_PREFIX = "tailored-";

// The slug a raw playbook-proposed handle actually lands under.
export function tailoredCanvasSlug(raw) {
    const s = sanitizeSlug(raw);
    if (!s) return "";
    return s.startsWith(SLUG_PREFIX) ? s : sanitizeSlug(`${SLUG_PREFIX}${s}`);
}

// Shared coercion pipeline: accept the `canvases` arg in every form a model
// (or the persisted transcript) can carry it — the whole list as a JSON
// string, individual entries double-encoded, `rows` double-encoded — and
// normalize slugs. Used by both execute() and parseTailoredCanvasesArgs so
// the two can never drift.
function coerceCanvasList(rawCanvases) {
    const list = coerceJsonArg(rawCanvases);
    return (Array.isArray(list) ? list : [])
        .map((c) => coerceJsonArg(c))
        .filter((c) => c && typeof c === "object")
        .map((c) => ({ ...c, slug: tailoredCanvasSlug(c.slug), rows: coerceJsonArg(c.rows) }));
}

// Structural cleaning — the acceptance contract for one canvas entry:
// a valid slug, a title, and at least one row of cells. Applied to the
// coerced list with slug dedupe and a set cap; what survives is exactly
// what the profile view offers to add.
const MAX_CANVASES = 4;
const SLUG_RE = /^[a-z0-9][a-z0-9-_]{0,63}$/;

function cleanCanvas(raw) {
    if (!raw || typeof raw !== "object") return null;
    const slug = typeof raw.slug === "string" && SLUG_RE.test(raw.slug) ? raw.slug : null;
    const title = typeof raw.title === "string" && raw.title.trim() ? raw.title.trim() : null;
    if (!slug || !title) return null;
    const rows = normalizeRows(raw);
    if (!rows.length) return null;
    return {
        slug,
        title,
        description: typeof raw.description === "string" && raw.description.trim()
            ? raw.description.trim()
            : null,
        rows,
    };
}

function cleanCanvasSet(rawCanvases) {
    const seen = new Set();
    const out = [];
    for (const raw of Array.isArray(rawCanvases) ? rawCanvases : []) {
        const c = cleanCanvas(raw);
        if (!c || seen.has(c.slug)) continue;
        seen.add(c.slug);
        out.push(c);
        if (out.length >= MAX_CANVASES) break;
    }
    return out;
}

// ── Kernel gate ──────────────────────────────────────────────────────
// A palette command EXISTING says nothing about it RUNNING: a user-saved
// kernel's spec can drift from the live schema and 400 on every open
// (exactly what a tailored canvas must not ship). Before accepting a
// set, run each referenced kernel once, tenant-wide, and reject the
// call on any failure so the model recomposes with healthy kernels.
// Deterministic — no sub-agent: runnability is answered by running.
//
// Only kernels made of deterministic-read steps (analytics_query /
// python) are executed. A kernel with an mcp_tool step (possible side
// effects) or an agent step (an LLM call per verification) is skipped
// and reported as unverified rather than run behind the user's back; a
// stepless kernel has nothing to verify and counts as unverified too.
const GATE_TIMEOUT_MS = 20000;
const GATE_RUNNABLE_STEP_KINDS = new Set(["analytics_query", "python"]);

function renderDataPresent(renderData) {
    if (Array.isArray(renderData)) return renderData.length > 0;
    if (renderData && typeof renderData === "object") return Object.keys(renderData).length > 0;
    return renderData != null;
}

// Exported for tests (the timeout and step-kind edges are hard to reach
// through execute()); execute() is the only production caller.
export async function gateKernel(definition, { scheduler: sharedScheduler = null } = {}) {
    let command = "(unknown)";
    let scheduler = sharedScheduler;
    let ownsScheduler = false;
    try {
        const resolvedDefinition = asResolvedKernel(definition);
        const kernel = resolvedDefinition.body;
        command = kernel?.command || command;
        const steps = Array.isArray(kernel?.steps) ? kernel.steps : [];
        if (!steps.length || !steps.every((s) => GATE_RUNNABLE_STEP_KINDS.has(s?.kind))) {
            return { command, status: "unverified" };
        }
        if (!scheduler) {
            scheduler = new CanvasRunScheduler({
                concurrency: 1,
                timeoutMs: GATE_TIMEOUT_MS,
            });
            ownsScheduler = true;
        }
        const handle = scheduler.schedule(
            ({ signal, runId }) => runKernel({
                resolvedDefinition,
                // This model-operated background gate has no authoritative
                // interactive provenance of its own.
                runContext: createUnknownRunContext({
                    surface: RUN_SURFACES.BACKGROUND,
                }),
                params: { scope: [] },
                signal,
                runId,
                timeoutMs: 0,
            }),
            {
                id: "tailored-kernel-gate",
                timeoutMs: GATE_TIMEOUT_MS,
                metadata: { surface: RUN_SURFACES.BACKGROUND },
            },
        );
        const run = await handle.promise;
        if (!run?.ok) {
            const reasonCode = run?.reason?.code;
            if (run?.cancelled && reasonCode !== KERNEL_RUN_CANCEL_REASON.TIMEOUT) {
                return {
                    command,
                    status: "cancelled",
                    message: run?.reason?.message || "validation run was cancelled",
                    reasonCode,
                };
            }
            return {
                command,
                status: "error",
                message: reasonCode === KERNEL_RUN_CANCEL_REASON.TIMEOUT
                    ? `timed out after ${GATE_TIMEOUT_MS / 1000}s`
                    : run?.error?.message || String(run?.error || "kernel run failed"),
            };
        }
        return { command, status: renderDataPresent(run.renderData) ? "ok" : "empty" };
    } catch (err) {
        if (err?.cancelled === true && err?.reason?.code !== KERNEL_RUN_CANCEL_REASON.TIMEOUT) {
            return {
                command,
                status: "cancelled",
                message: err.reason?.message || err.message || "validation run was cancelled",
                reasonCode: err.reason?.code,
            };
        }
        return { command, status: "error", message: err?.message || String(err) };
    } finally {
        if (ownsScheduler) scheduler.dispose();
    }
}

// Stored/canvas Python runs share one published realm. One scheduler with
// concurrency 1, awaited one definition at a time, prevents a timeout from
// tearing down sibling gates and also keeps queue time out of later deadlines.
export async function gateKernelsSequentially(definitions) {
    const scheduler = new CanvasRunScheduler({
        concurrency: 1,
        timeoutMs: GATE_TIMEOUT_MS,
    });
    const results = [];
    try {
        for (const definition of definitions) {
            const result = await gateKernel(definition, { scheduler });
            results.push(result);
            if (result.status === "cancelled") break;
        }
        return results;
    } finally {
        scheduler.dispose();
    }
}

// Parse a set_tailored_canvases args blob (live object, or the persisted /
// streamed-JSON string form off a chat transcript) into the entries the
// scout profile view offers: { profile, canvases: [{ slug, title,
// description, rows }] }, or null when nothing was accepted. Runs the
// exact execute() pipeline (coercion, slug prefixing, structural cleaning,
// dedupe, cap), so the view offers exactly the set the tool accepted —
// `rows` included, because clicking an entry materializes it as a normal
// user canvas from these parsed args.
export function parseTailoredCanvasesArgs(args) {
    let a = args;
    if (typeof a === "string") { try { a = JSON.parse(a); } catch { return null; } }
    if (!a || typeof a !== "object") return null;
    const clean = cleanCanvasSet(coerceCanvasList(a.canvases));
    if (!clean.length) return null;
    return {
        profile: typeof a.profile === "string" ? a.profile.trim() : "",
        canvases: clean.map((c) => ({ slug: c.slug, title: c.title, description: c.description || "", rows: c.rows })),
    };
}

export const setTailoredCanvasesTool = {
    name: "set_tailored_canvases",
    label: "Set tailored canvases",
    description:
        "FINAL steps of the tenant-profiling playbook ONLY. After you've sampled the tenant's data and characterized what the org does with AI, call this exactly once with 2-4 tailored canvases — each a bundle of existing kernel commands (from the palette in the playbook prompt) arranged in rows, with titles/descriptions/captions grounded in THIS org's real usage. Every referenced kernel is RUN once against live data before the set is accepted; if any fail (e.g. a saved kernel whose spec has drifted from the schema), the call errors with the failing commands — recompose with healthy kernels (built-ins are safe bets) and call again. The accepted set renders in the Tenant profile view, where the user adds each canvas with a click. This is NOT a render or analysis tool — never call it during ordinary questions, and never invent kernel commands.",
    parameters: Type.Object({
        profile: Type.String({
            description:
                "2-3 sentence characterization of what this org actually does with AI — the same profile passed to set_starter_cards.",
        }),
        canvases: Type.Array(
            Type.Object({
                slug: Type.String({
                    description: "Short kebab-case handle, e.g. 'agent-fleet-watch'. A 'tailored-' prefix is added automatically.",
                }),
                title: Type.String({ description: "Short human label, 2-5 words." }),
                description: Type.String({
                    description:
                        "1-2 sentences: what this canvas watches and why it matters for THIS org, grounded in the profile.",
                }),
                rows: Type.Array(
                    Type.Object({
                        cells: Type.Array(
                            Type.Object({
                                command: Type.String({
                                    description: "An EXISTING kernel command from the palette — never invented.",
                                }),
                                caption: Type.Optional(Type.String({
                                    description: "Caption shown above the widget — tailor it to what the org should look for.",
                                })),
                            }),
                            { description: "Cells in a row render side by side. 1-2 per row.", minItems: 1, maxItems: 3 },
                        ),
                    }),
                    { description: "Rows of cells, top to bottom. 2-5 rows.", minItems: 1, maxItems: 6 },
                ),
            }),
            { description: "2-4 tailored canvases.", minItems: 1, maxItems: 4 },
        ),
    }),
    async execute(_id, args) {
        // Loosely-nested params can arrive double-encoded on some model
        // families — coerce at the boundary like the canvas tools do,
        // then clean to the acceptance contract before any checks.
        const clean = cleanCanvasSet(coerceCanvasList(args?.canvases));
        if (!clean.length) {
            const message =
                "set_tailored_canvases needs a non-empty `canvases` array where each canvas has a slug, a title, and at least one row of cells referencing existing kernel commands.";
            return {
                content: [{ type: "text", text: message }],
                details: { error: message },
                isError: true,
            };
        }
        const catalog = new Map(listKernelCatalog(null).map((definition) => [
            definition.body.command,
            definition,
        ]));
        const referenced = new Set();
        const unknown = new Set();
        for (const c of clean) {
            for (const row of c.rows) {
                for (const cell of row.cells) {
                    if (catalog.has(cell.command)) referenced.add(cell.command);
                    else unknown.add(cell.command);
                }
            }
        }
        if (unknown.size) {
            const message =
                `Unknown kernel command(s): ${[...unknown].join(", ")}. ` +
                `Canvases may only reference existing kernels — valid commands: ${[...catalog.keys()].sort().join(", ")}.`;
            return {
                content: [{ type: "text", text: message }],
                details: { error: message },
                isError: true,
            };
        }
        // Kernel gate: every referenced kernel must survive a live run
        // before the set is accepted (see gateKernel above).
        const kernels = [];
        const resolutionFailures = [];
        for (const command of referenced) {
            try {
                const resolved = await resolveKernelByCommandAsync(null, command);
                if (!resolved) {
                    resolutionFailures.push({
                        command,
                        status: "unavailable",
                        message: "kernel definition is no longer available",
                    });
                } else {
                    kernels.push(resolved);
                }
            } catch (error) {
                resolutionFailures.push({
                    command,
                    status: "unavailable",
                    message: error?.message || String(error),
                });
            }
        }
        if (resolutionFailures.length) {
            const message =
                "Kernel gate could not load every referenced kernel definition. " +
                resolutionFailures.map((failure) =>
                    `\`${failure.command}\`: ${failure.message}`).join("; ") +
                ". Nothing was accepted; retry after the cloud catalog is available.";
            return {
                content: [{ type: "text", text: message }],
                details: {
                    error: message,
                    kind: "tailoredCanvasesResolution",
                    failures: resolutionFailures,
                },
                isError: true,
            };
        }
        const gate = await gateKernelsSequentially(kernels);
        const cancellations = gate.filter((result) => result.status === "cancelled");
        if (cancellations.length) {
            const message =
                "Kernel gate validation was interrupted when the shared execution realm restarted. " +
                cancellations.map((result) => `\`${result.command}\`: ${result.message}`).join("; ") +
                ". No kernel was classified as broken and nothing was accepted; retry the same set.";
            return {
                content: [{ type: "text", text: message }],
                details: {
                    error: message,
                    kind: "tailoredCanvasesGateInterrupted",
                    cancellations,
                },
                isError: true,
            };
        }
        const failures = gate.filter((g) => g.status === "error");
        if (failures.length) {
            const message =
                `Kernel gate: ${failures.length} referenced kernel(s) failed a live run — ` +
                failures.map((f) => `\`${f.command}\`: ${f.message}`).join("; ") +
                ". These would render as broken widgets. Recompose the affected canvases with kernels that run (the built-ins are safe bets) and call set_tailored_canvases again. Nothing was accepted.";
            return {
                content: [{ type: "text", text: message }],
                details: { error: message, kind: "tailoredCanvasesGate", failures },
                isError: true,
            };
        }
        const count = clean.length;
        const empties = gate.filter((g) => g.status === "empty").map((g) => g.command);
        const unverified = gate.filter((g) => g.status === "unverified").map((g) => g.command);
        const verified = gate.length - unverified.length;
        const notes = [
            verified ? `${verified} referenced kernel${verified === 1 ? "" : "s"} passed the live-run gate.` : null,
            empties.length ? `Note: ${empties.join(", ")} returned no data tenant-wide in the default window — swap them (another set_tailored_canvases call replaces this one) if better-populated kernels fit.` : null,
            unverified.length ? `Not live-verified (stepless or mcp/agent steps, which the gate never runs): ${unverified.join(", ")}.` : null,
        ].filter(Boolean);
        return {
            content: [
                {
                    type: "text",
                    text: [
                        `Accepted ${count} tailored canvas${count === 1 ? "" : "es"} — they render in the Tenant profile view, where the user adds each to their canvas set with a click.`,
                        ...notes,
                    ].join(" "),
                },
            ],
            details: { kind: "tailoredCanvases", count, profile: args?.profile },
        };
    },
};
